import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  isHuaweiConfigured,
  getHuaweiAuthUrl,
  getMockDeviceInfo,
  getStoredTokens,
  clearTokens,
} from "@/services/huaweiHealth";
import {
  Watch,
  Smartphone,
  Bluetooth,
  BluetoothConnected,
  RefreshCw,
  Link,
  Unlink,
  Battery,
  CheckCircle2,
  AlertCircle,
  Settings,
  ChevronRight,
  Heart,
  Activity,
  Moon,
  Footprints,
  Flame,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

// Supported integrations
const integrations = [
  {
    id: "huawei",
    name: "Huawei Health",
    description: "Smartwatch, band e bilance Huawei",
    icon: Watch,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    available: true,
    dataTypes: ["Passi", "Frequenza cardiaca", "Sonno", "SpO2", "Stress", "Calorie"],
  },
  {
    id: "ringconn",
    name: "RingConn",
    description: "Smart Ring RingConn",
    icon: Activity,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    available: false,
    dataTypes: ["Frequenza cardiaca", "Sonno", "SpO2", "HRV", "Temperatura"],
    comingSoon: true,
  },
  {
    id: "fitbit",
    name: "Fitbit",
    description: "Tracker e smartwatch Fitbit",
    icon: Watch,
    color: "text-teal-500",
    bgColor: "bg-teal-500/10",
    available: false,
    dataTypes: ["Passi", "Frequenza cardiaca", "Sonno", "Attività"],
  },
  {
    id: "garmin",
    name: "Garmin",
    description: "Dispositivi Garmin",
    icon: Watch,
    color: "text-blue-600",
    bgColor: "bg-blue-600/10",
    available: false,
    dataTypes: ["Passi", "Frequenza cardiaca", "Sonno", "GPS", "VO2 Max"],
  },
];

export const PatientDevicesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [connectedDevices, setConnectedDevices] = useState({});
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [autoSync, setAutoSync] = useState(true);

  // Check for OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      handleOAuthCallback(code);
    }
  }, [searchParams]);

  // Load connected devices status
  useEffect(() => {
    const tokens = getStoredTokens();
    if (tokens && !tokens.expired) {
      setConnectedDevices({ huawei: true });
      // Get device info (mock for now)
      setDeviceInfo(getMockDeviceInfo());
    }
  }, []);

  const handleOAuthCallback = async (code) => {
    setIsLoading(true);
    try {
      // In production, exchange code for token
      // For demo, simulate success
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store mock tokens
      localStorage.setItem('huawei_tokens', JSON.stringify({
        access_token: 'demo_token',
        refresh_token: 'demo_refresh',
        expires_in: 3600,
        stored_at: Date.now(),
      }));
      
      setConnectedDevices({ huawei: true });
      setDeviceInfo(getMockDeviceInfo());
      
      toast.success("Huawei Health connesso!", {
        description: "I tuoi dati salute sono ora sincronizzati"
      });
      
      // Clear URL params
      navigate('/patient/devices', { replace: true });
    } catch (error) {
      toast.error("Errore di connessione", {
        description: "Impossibile collegare Huawei Health"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = (integrationId) => {
    if (integrationId === 'huawei') {
      if (isHuaweiConfigured()) {
        // Redirect to Huawei OAuth
        window.location.href = getHuaweiAuthUrl();
      } else {
        // Demo mode - simulate connection
        setIsLoading(true);
        setTimeout(() => {
          localStorage.setItem('huawei_tokens', JSON.stringify({
            access_token: 'demo_token',
            refresh_token: 'demo_refresh',
            expires_in: 3600,
            stored_at: Date.now(),
          }));
          setConnectedDevices({ huawei: true });
          setDeviceInfo(getMockDeviceInfo());
          setIsLoading(false);
          toast.success("Modalità Demo attiva", {
            description: "Connessione simulata - Configura le credenziali Huawei per dati reali"
          });
        }, 1500);
      }
    } else {
      toast.info("Prossimamente", {
        description: `L'integrazione con ${integrationId} sarà disponibile a breve`
      });
    }
  };

  const handleDisconnect = (integrationId) => {
    clearTokens();
    setConnectedDevices({});
    setDeviceInfo(null);
    toast.success("Dispositivo disconnesso");
  };

  const handleSync = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setDeviceInfo(getMockDeviceInfo());
    setIsLoading(false);
    toast.success("Dati sincronizzati", {
      description: "Tutti i parametri sono aggiornati"
    });
  };

  const isConfigured = isHuaweiConfigured();

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Dispositivi Connessi</h1>
        <p className="text-muted-foreground">Collega i tuoi wearable per sincronizzare i dati salute</p>
      </div>

      {/* Demo Mode Banner */}
      {!isConfigured && (
        <Card className="bg-warning/10 border-warning/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-warning mt-0.5" />
              <div>
                <p className="font-medium text-warning">Modalità Demo</p>
                <p className="text-sm text-muted-foreground">
                  Le credenziali Huawei non sono configurate. I dati mostrati sono simulati.
                  Aggiungi REACT_APP_HUAWEI_CLIENT_ID e REACT_APP_HUAWEI_CLIENT_SECRET nel file .env per dati reali.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connected Device Info */}
      {deviceInfo?.connected && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BluetoothConnected className="h-5 w-5 text-primary" />
                Dispositivo Attivo
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={isLoading}
                className="gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Sincronizza
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <Watch className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{deviceInfo.device.name}</h3>
                <p className="text-sm text-muted-foreground">{deviceInfo.device.model}</p>
                <div className="flex items-center gap-4 mt-2">
                  <Badge variant="outline" className="gap-1">
                    <Battery className="h-3 w-3" />
                    {deviceInfo.device.battery}%
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Ultimo sync: {format(new Date(deviceInfo.device.lastSync), "HH:mm", { locale: it })}
                  </span>
                </div>
              </div>
              <Badge className="bg-success/10 text-success border-success/20 gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Connesso
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sync Settings */}
      {connectedDevices.huawei && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Impostazioni Sincronizzazione
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Sincronizzazione automatica</Label>
                <p className="text-sm text-muted-foreground">Sincronizza i dati ogni ora</p>
              </div>
              <Switch checked={autoSync} onCheckedChange={setAutoSync} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <Label>Dati sincronizzati</Label>
                <p className="text-sm text-muted-foreground">Seleziona quali dati importare</p>
              </div>
              <Button variant="ghost" size="sm">
                Configura
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Integrations */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Integrazioni Disponibili</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {integrations.map((integration) => {
            const Icon = integration.icon;
            const isConnected = connectedDevices[integration.id];
            
            return (
              <Card
                key={integration.id}
                className={cn(
                  "transition-all",
                  isConnected && "border-primary/50 bg-primary/5",
                  !integration.available && "opacity-60"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={cn("p-3 rounded-xl", integration.bgColor)}>
                      <Icon className={cn("h-6 w-6", integration.color)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{integration.name}</h3>
                        {integration.comingSoon && (
                          <Badge variant="secondary" className="text-xs">Prossimamente</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{integration.description}</p>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {integration.dataTypes.slice(0, 4).map((type, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {type}
                          </Badge>
                        ))}
                        {integration.dataTypes.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{integration.dataTypes.length - 4}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="mt-3">
                        {isConnected ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisconnect(integration.id)}
                            className="gap-2 text-destructive hover:text-destructive"
                          >
                            <Unlink className="h-4 w-4" />
                            Disconnetti
                          </Button>
                        ) : (
                          <Button
                            variant={integration.available ? "default" : "secondary"}
                            size="sm"
                            onClick={() => handleConnect(integration.id)}
                            disabled={!integration.available || isLoading}
                            className="gap-2"
                          >
                            {isLoading && integration.id === 'huawei' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Link className="h-4 w-4" />
                            )}
                            {integration.available ? "Connetti" : "Non disponibile"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Data Types Legend */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">Parametri Monitorabili</CardTitle>
          <CardDescription>Dati che puoi sincronizzare dai tuoi dispositivi</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Footprints className="h-4 w-4 text-primary" />
              <span className="text-sm">Passi e distanza</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-destructive" />
              <span className="text-sm">Frequenza cardiaca</span>
            </div>
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4 text-info" />
              <span className="text-sm">Qualità sonno</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-success" />
              <span className="text-sm">SpO2 e stress</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-warning" />
              <span className="text-sm">Calorie bruciate</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-secondary" />
              <span className="text-sm">Attività fisica</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientDevicesPage;
